import React, { useState, useEffect } from 'react';
import { 
  CheckCircle, 
  AlertCircle, 
  X, 
  Info, 
  AlertTriangle, 
  ChevronLeft, 
  MessageSquare, 
  Printer,
  Trash2,
  RefreshCw,
  Save,
  FileText,
  Package,
} from 'lucide-react';

// Notification types
type NotificationType = 'success' | 'error' | 'warning' | 'info';

interface Notification {
  id: string;
  type: NotificationType;
  title?: string;
  message: string;
  duration?: number;
  timestamp: number;
}

// Toast Notification Component
const ToastNotification: React.FC<{
  notification: Notification;
  onClose: (id: string) => void;
}> = ({ notification, onClose }) => {
  useEffect(() => {
    const duration = notification.duration || (notification.type === 'error' ? 8000 : 4000);
    
    const timer = setTimeout(() => {
      onClose(notification.id);
    }, duration);
    
    return () => clearTimeout(timer);
  }, [notification, onClose]);

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle className="text-green-500" size={24} />;
      case 'error':
        return <AlertCircle className="text-red-500" size={24} />;
      case 'warning':
        return <AlertTriangle className="text-yellow-500" size={24} />;
      case 'info':
        return <Info className="text-blue-500" size={24} />;
    }
  };

  const getStyles = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
    }
  };

  return (
    <div className={`flex items-start space-x-4 p-4 rounded-lg border shadow-lg ${getStyles()} transform transition-all duration-300 animate-slide-in min-w-80 max-w-md`}>
      {getIcon()}
      <div className="flex-1 min-w-0">
        {notification.title && (
          <h4 className="font-semibold text-gray-900 mb-1 text-sm">{notification.title}</h4>
        )}
        <p className="text-sm text-gray-700 break-words">{notification.message}</p>
      </div>
      <button
        onClick={() => onClose(notification.id)}
        className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 p-1"
        aria-label="Lukk melding"
      >
        <X size={16} />
      </button>
    </div>
  );
};

// Toast Container
const ToastContainer: React.FC<{
  notifications: Notification[];
  onClose: (id: string) => void;
}> = ({ notifications, onClose }) => {
  const maxVisible = 5;
  const visibleNotifications = notifications.slice(-maxVisible);

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 max-w-md">
      {visibleNotifications.map((notification) => (
        <ToastNotification
          key={notification.id}
          notification={notification}
          onClose={onClose}
        />
      ))}
      {notifications.length > maxVisible && (
        <div className="text-xs text-gray-500 text-center p-2">
          +{notifications.length - maxVisible} flere meldinger
        </div>
      )}
    </div>
  );
};

// Hook for managing notifications
const useNotifications = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const generateId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const showNotification = (
    type: NotificationType,
    message: string,
    title?: string,
    duration?: number
  ) => {
    const now = Date.now();
    
    const recentDuplicate = notifications.find(n => 
      n.message === message && 
      n.type === type && 
      (now - n.timestamp) < 2000
    );
    
    if (recentDuplicate) {
      console.log('Duplikat notifikasjon ignorert:', message);
      return;
    }

    const id = generateId();
    const notification: Notification = {
      id,
      type,
      title,
      message,
      duration,
      timestamp: now
    };
    
    setNotifications(prev => {
      const newNotifications = [...prev, notification];
      return newNotifications.slice(-10);
    });
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const clearNotificationsByType = (type: NotificationType) => {
    setNotifications(prev => prev.filter(n => n.type !== type));
  };

  const showSuccess = (message: string, title?: string, duration?: number) => {
    showNotification('success', message, title, duration || 3000);
  };

  const showError = (message: string, title?: string, duration?: number) => {
    showNotification('error', message, title, duration || 8000);
  };

  const showWarning = (message: string, title?: string, duration?: number) => {
    showNotification('warning', message, title, duration || 5000);
  };

  const showInfo = (message: string, title?: string, duration?: number) => {
    showNotification('info', message, title, duration || 4000);
  };

  return {
    notifications,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    removeNotification,
    clearAllNotifications,
    clearNotificationsByType
  };
};

// Base Dialog Component
interface BaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
}

const BaseDialog: React.FC<BaseDialogProps> = ({ 
  isOpen, 
  onClose, 
  children, 
  maxWidth = 'md' 
}) => {
  if (!isOpen) return null;

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md', 
    lg: 'max-w-lg',
    xl: 'max-w-xl'
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-xl shadow-2xl ${maxWidthClasses[maxWidth]} w-full transform transition-all duration-300 scale-100`}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

// Confirmation Dialog - for viktige handlinger
interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  type?: 'warning' | 'danger' | 'info' | 'success';
  confirmText?: string;
  cancelText?: string;
  icon?: React.ReactNode;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'info',
  confirmText = 'Bekreft',
  cancelText = 'Avbryt',
  icon
}) => {
  const getTypeStyles = () => {
    switch (type) {
      case 'danger':
        return {
          headerBg: 'bg-gradient-to-r from-red-50 to-red-100',
          iconColor: 'text-red-600',
          confirmButton: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
          defaultIcon: <AlertCircle size={24} />
        };
      case 'warning':
        return {
          headerBg: 'bg-gradient-to-r from-yellow-50 to-yellow-100',
          iconColor: 'text-yellow-600',
          confirmButton: 'bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500',
          defaultIcon: <AlertTriangle size={24} />
        };
      case 'success':
        return {
          headerBg: 'bg-gradient-to-r from-green-50 to-green-100',
          iconColor: 'text-green-600',
          confirmButton: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
          defaultIcon: <CheckCircle size={24} />
        };
      default:
        return {
          headerBg: 'bg-gradient-to-r from-blue-50 to-blue-100',
          iconColor: 'text-blue-600',
          confirmButton: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
          defaultIcon: <Info size={24} />
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <BaseDialog isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <div className={`px-6 py-4 border-b border-gray-200 rounded-t-xl ${styles.headerBg}`}>
        <div className="flex items-center space-x-3">
          <div className={styles.iconColor}>
            {icon || styles.defaultIcon}
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <p className="text-gray-700 leading-relaxed">{message}</p>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
        >
          {cancelText}
        </button>
        <button
          onClick={() => {
            onConfirm();
            onClose();
          }}
          className={`px-4 py-2.5 text-white rounded-lg transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 ${styles.confirmButton}`}
        >
          {confirmText}
        </button>
      </div>
    </BaseDialog>
  );
};

// Info Dialog - for informasjon og tips
interface InfoDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  icon?: React.ReactNode;
  type?: 'info' | 'success' | 'tip';
}

const InfoDialog: React.FC<InfoDialogProps> = ({
  isOpen,
  onClose,
  title,
  message,
  icon,
  type = 'info'
}) => {
  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          headerBg: 'bg-gradient-to-r from-green-50 to-green-100',
          iconColor: 'text-green-600',
          defaultIcon: <CheckCircle size={24} />
        };
      case 'tip':
        return {
          headerBg: 'bg-gradient-to-r from-purple-50 to-purple-100',
          iconColor: 'text-purple-600',
          defaultIcon: <Info size={24} />
        };
      default:
        return {
          headerBg: 'bg-gradient-to-r from-blue-50 to-blue-100',
          iconColor: 'text-blue-600',
          defaultIcon: <Info size={24} />
        };
    }
  };

  const styles = getTypeStyles();

  return (
    <BaseDialog isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <div className={`px-6 py-4 border-b border-gray-200 rounded-t-xl ${styles.headerBg}`}>
        <div className="flex items-center space-x-3">
          <div className={styles.iconColor}>
            {icon || styles.defaultIcon}
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <p className="text-gray-700 leading-relaxed">{message}</p>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
        >
          OK
        </button>
      </div>
    </BaseDialog>
  );
};

// Service Label Dialog - spesiell for merkelapp
interface ServiceLabelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  customerName: string;
}

const ServiceLabelDialog: React.FC<ServiceLabelDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  customerName
}) => {
  return (
    <BaseDialog isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 rounded-t-xl bg-gradient-to-r from-blue-50 to-blue-100">
        <div className="flex items-center space-x-3">
          <div className="text-blue-600">
            <Printer size={24} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Skriv ut merkelapp</h3>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <Info className="text-blue-600 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-medium text-blue-800">
                Ordre lagres til Service
              </p>
              <p className="text-sm text-blue-700 mt-1">
                Vil du skrive ut merkelapp for <strong>{customerName}</strong>?
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-white p-2 rounded-lg border">
              <FileText className="text-gray-600" size={20} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Merkelapp inneholder:</p>
              <p className="text-xs text-gray-600">Kunde, telefon, maskin, beskrivelse og kommentarer</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
        >
          Ikke nå
        </button>
        <button
          onClick={() => {
            onConfirm();
            onClose();
          }}
          className="px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center space-x-2"
        >
          <Printer size={16} />
          <span>Skriv ut</span>
        </button>
      </div>
    </BaseDialog>
  );
};

// Bestilling Label Dialog - for når ordre flyttes fra "Bestilling" til "Klar til henting"
interface BestillingLabelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  customerName: string;
  items: string; // Beskrivelse av bestilte varer
}

const BestillingLabelDialog: React.FC<BestillingLabelDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  customerName,
  items
}) => {
  return (
    <BaseDialog isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 rounded-t-xl bg-gradient-to-r from-orange-50 to-orange-100">
        <div className="flex items-center space-x-3">
          <div className="text-orange-600">
            <Printer size={24} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Skriv ut merkelapp for henting</h3>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <Package className="text-orange-600 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-medium text-orange-800">
                Varer er kommet inn og flyttes til "Klar til henting"
              </p>
              <p className="text-sm text-orange-700 mt-1">
                Vil du skrive ut merkelapp for <strong>{customerName}</strong>?
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-white p-2 rounded-lg border">
              <FileText className="text-gray-600" size={20} />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">Merkelapp inneholder:</p>
              <p className="text-xs text-gray-600">Kunde, telefon, bestilte varer og antall</p>
              <p className="text-xs text-gray-500 mt-1 italic">"{items}"</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
        >
          Ikke nå
        </button>
        <button
          onClick={() => {
            onConfirm();
            onClose();
          }}
          className="px-4 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium flex items-center space-x-2"
        >
          <Printer size={16} />
          <span>Skriv ut</span>
        </button>
      </div>
    </BaseDialog>
  );
};

// SMS Reminder Dialog
interface SmsReminderDialogProps {
  isOpen: boolean;
  onClose: () => void;
  customerName: string;
  customerPhone: string;
}

const formatPhoneNumber = (phone: string) => {
  if (!phone || phone.length !== 8) return phone;
  return `${phone.slice(0, 3)} ${phone.slice(3, 5)} ${phone.slice(5, 8)}`;
};

const SmsReminderDialog: React.FC<SmsReminderDialogProps> = ({
  isOpen,
  onClose,
  customerName,
  customerPhone
}) => {
  return (
    <BaseDialog isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 rounded-t-xl bg-gradient-to-r from-green-50 to-green-100">
        <div className="flex items-center space-x-3">
          <div className="text-green-600">
            <MessageSquare size={24} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">SMS Påminnelse</h3>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <CheckCircle className="text-green-600 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-medium text-green-800">
                Ordre flyttet til "Klar til henting"
              </p>
              <p className="text-sm text-green-700 mt-1">
                Husk å sende SMS til kunden om at ordren er klar.
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Kunde:</span>
              <span className="text-sm font-medium text-gray-900">{customerName}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Telefon:</span>
              <span className="text-xl font-bold text-green-600">{formatPhoneNumber(customerPhone)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end">
        <button
          onClick={onClose}
          className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
        >
          Forstått
        </button>
      </div>
    </BaseDialog>
  );
};

// Progress Dialog - for import og lignende
interface ProgressDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  current: number;
  total: number;
}

const ProgressDialog: React.FC<ProgressDialogProps> = ({
  isOpen,
  title,
  message,
  current,
  total
}) => {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <BaseDialog isOpen={isOpen} onClose={() => {}}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 rounded-t-xl bg-gradient-to-r from-blue-50 to-blue-100">
        <div className="flex items-center space-x-3">
          <div className="text-blue-600">
            <RefreshCw size={24} className="animate-spin" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <p className="text-gray-700 mb-4">{message}</p>
        
        {/* Progress bar */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{current} av {total}</span>
            <span className="text-gray-900 font-medium">{percentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div 
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-300" 
              style={{ width: `${percentage}%` }}
            ></div>
          </div>
        </div>
      </div>
    </BaseDialog>
  );
};

// Move Backwards Dialog
interface MoveBackwardsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  caseName: string;
  fromCategory: string;
  toCategory: string;
}

const MoveBackwardsDialog: React.FC<MoveBackwardsDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  caseName,
  fromCategory,
  toCategory
}) => {
  return (
    <BaseDialog isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 rounded-t-xl bg-gradient-to-r from-yellow-50 to-yellow-100">
        <div className="flex items-center space-x-3">
          <div className="text-yellow-600">
            <ChevronLeft size={24} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Flytt bakover i prosessen</h3>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="text-yellow-600 mt-0.5" size={20} />
            <div>
              <p className="text-sm font-medium text-yellow-800">
                ⚠️ ADVARSEL - Flytter bakover
              </p>
              <p className="text-sm text-yellow-700 mt-1">
                Du er i ferd med å flytte ordren tilbake i arbeidsflyten
              </p>
            </div>
          </div>
        </div>
        
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-1">Kunde:</p>
            <p className="font-medium text-gray-900">{caseName}</p>
          </div>
          
          <div className="flex items-center space-x-3">
            <div className="flex-1 bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Fra:</p>
              <p className="font-medium text-red-700">{fromCategory}</p>
            </div>
            <ChevronLeft className="text-gray-400" size={20} />
            <div className="flex-1 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-xs text-gray-600 mb-1">Til:</p>
              <p className="font-medium text-yellow-700">{toCategory}</p>
            </div>
          </div>
          
          <p className="text-sm text-gray-600">
            Er du sikker på at dette er riktig?
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
        >
          Avbryt
        </button>
        <button
          onClick={() => {
            onConfirm();
            onClose();
          }}
          className="px-4 py-2.5 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors font-medium flex items-center space-x-2"
        >
          <ChevronLeft size={16} />
          <span>Flytt tilbake</span>
        </button>
      </div>
    </BaseDialog>
  );
};

// Archive Dialog - for arkivering med utlevert-av
interface ArchiveDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (person: string) => void;
  customerName: string;
  persons: string[];
}

const ArchiveDialog: React.FC<ArchiveDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  customerName,
  persons
}) => {
  const [selectedPerson, setSelectedPerson] = React.useState<string>('');

  return (
    <BaseDialog isOpen={isOpen} onClose={onClose}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 rounded-t-xl bg-gradient-to-r from-green-50 to-green-100">
        <div className="flex items-center space-x-3">
          <div className="text-green-600">
            <Save size={24} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Arkiver og marker som utlevert</h3>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 py-6">
        <p className="text-gray-700 mb-4">
          Hvem leverte ut maskinen til <strong>{customerName}</strong>?
        </p>
        
        <div className="space-y-2">
          {persons.map((person) => (
            <button
              key={person}
              onClick={() => setSelectedPerson(person)}
              className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${
                selectedPerson === person
                  ? 'border-green-500 bg-green-50 text-green-900'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{person}</span>
                {selectedPerson === person && (
                  <CheckCircle className="text-green-600" size={20} />
                )}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="px-6 py-4 bg-gray-50 rounded-b-xl flex justify-end space-x-3">
        <button
          onClick={onClose}
          className="px-4 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
        >
          Avbryt
        </button>
        <button
          onClick={() => {
            if (selectedPerson) {
              onConfirm(selectedPerson);
              onClose();
            }
          }}
          disabled={!selectedPerson}
          className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center space-x-2"
        >
          <Save size={16} />
          <span>Arkiver</span>
        </button>
      </div>
    </BaseDialog>
  );
};

// Export all components
export {
  ToastContainer,
  useNotifications,
  ConfirmDialog,
  InfoDialog,
  ServiceLabelDialog,
  BestillingLabelDialog,
  SmsReminderDialog,
  ProgressDialog,
  MoveBackwardsDialog,
  ArchiveDialog
};